/**
 * languages.ts — Danh sách ngôn ngữ giao diện được hỗ trợ.
 *
 * Tiếng Việt là ngôn ngữ NGUỒN (viết tay trong `locales/vi/*.json`) và là ngôn
 * ngữ dự phòng. Các ngôn ngữ khác là file tĩnh `locales/<code>/*.json` dịch từ
 * bản tiếng Việt (Claude dịch theo ngữ cảnh CRM, giữ nguyên {{biến}} và thẻ) —
 * không gọi API dịch lúc chạy. Khoá thiếu ở một ngôn ngữ tự rơi về tiếng Việt.
 * Kiểm tra độ phủ: `node scripts/i18n-check.mjs`.
 *
 * Thêm một ngôn ngữ:
 *   1. Thêm một dòng vào LANGUAGES (mã BCP-47).
 *   2. Thêm mã vào SUPPORTED_UI_LANGUAGES ở backend
 *      (`ecdp_backend_source/src/modules/ui-settings/ui-languages.ts`).
 *   3. Dịch toàn bộ `locales/vi/*.json` sang `locales/<code>/` (cùng khoá).
 */

export interface UiLanguage {
  /** Mã BCP-47 dùng cho i18next, `<html lang>` và Intl. */
  code: string
  /** Tên bằng chính ngôn ngữ đó — hiện trong ô chọn. */
  nativeName: string
  /** Tên tiếng Việt (gợi ý phụ). */
  viName: string
  flag: string
  /** Viết từ phải sang trái. */
  rtl?: boolean
}

export const SOURCE_LANGUAGE = 'vi'

export const LANGUAGES: UiLanguage[] = [
  { code: 'vi', nativeName: 'Tiếng Việt', viName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'en', nativeName: 'English', viName: 'Tiếng Anh', flag: '🇬🇧' },
  { code: 'zh-CN', nativeName: '简体中文', viName: 'Tiếng Trung (Giản thể)', flag: '🇨🇳' },
  { code: 'zh-TW', nativeName: '繁體中文', viName: 'Tiếng Trung (Phồn thể)', flag: '🇹🇼' },
  { code: 'ja', nativeName: '日本語', viName: 'Tiếng Nhật', flag: '🇯🇵' },
  { code: 'ko', nativeName: '한국어', viName: 'Tiếng Hàn', flag: '🇰🇷' },
  { code: 'th', nativeName: 'ไทย', viName: 'Tiếng Thái', flag: '🇹🇭' },
  { code: 'id', nativeName: 'Bahasa Indonesia', viName: 'Tiếng Indonesia', flag: '🇮🇩' },
  { code: 'km', nativeName: 'ភាសាខ្មែរ', viName: 'Tiếng Khmer', flag: '🇰🇭' },
  { code: 'lo', nativeName: 'ພາສາລາວ', viName: 'Tiếng Lào', flag: '🇱🇦' },
]

export const LANGUAGE_CODES = LANGUAGES.map((l) => l.code)

export function findLanguage(code: string | null | undefined): UiLanguage | undefined {
  if (!code) return undefined
  return LANGUAGES.find((l) => l.code === code)
}

export function isRtl(code: string | null | undefined): boolean {
  return !!findLanguage(code)?.rtl
}

/**
 * Khớp một mã ngôn ngữ trình duyệt (vd `en-US`, `zh-Hant-TW`, `pt-BR`) với danh
 * sách hỗ trợ: đúng mã → mã có vùng → mã gốc. Không khớp → undefined.
 */
export function matchLanguage(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  const lower = raw.toLowerCase()
  const exact = LANGUAGES.find((l) => l.code.toLowerCase() === lower)
  if (exact) return exact.code
  if (lower.startsWith('zh')) {
    return /hant|tw|hk|mo/.test(lower) ? 'zh-TW' : 'zh-CN'
  }
  const base = lower.split('-')[0]
  if (base === 'tl') return 'fil'
  if (base === 'iw') return 'he'
  if (base === 'in') return 'id'
  return LANGUAGES.find((l) => l.code.toLowerCase() === base)?.code
}
